/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import {
  Grid,
  Form,
  DateInput,
  Button,
  Icon,
  Message,
  Modal,
  Tooltip,
} from '@mairistem/composants';

import { ChampLieu } from '@/components/Common';
import { API_DATE_FORMAT, URL_AUTORITES_RECONNAISSANCE } from '@/constants';
import { useOptionsReferentiel } from '@/hooks';
import {
  createTimeInputHandler,
  buildEvenementReconnaissanceUpdatePayload,
  parseAdresseReconnaissance,
} from '@/utils';

interface ModalEvenementReconnaissanceProps {
  open: boolean;
  /** Identifiant de l'acte : construit l'URL de `service.update`. */
  identifiant?: number;
  /** L'évènement d'une reconnaissance est porté par l'ACTE, pas par l'enfant. */
  acte?: any;
  service?: { update: (data: Record<string, any>) => Promise<any> };
  onClose: () => void;
  onSaved: (acte: any) => void;
}

const CHAMPS_EDITABLES = [
  'dateReconnaissance',
  'heureReconnaissance',
  'minuteReconnaissance',
  'identifiantAuteurReconnaissance',
  'libelleAuteurReconnaissance',
  'codeAuteurReconnaissance',
];

/** Le parent qui n'est plus auteur, selon le code retenu. */
const parentSansObjet = (codeAuteur?: string) =>
  ({ P: 'la mère', M: 'le père' })[codeAuteur ?? ''];

/**
 * Bloc « INFORMATIONS SUR LA RECONNAISSANCE » en édition : date et heure de la
 * reconnaissance, auteur, lieu.
 *
 * Distinct de `ModalEvenementNaissance` car les données ne viennent pas du
 * même endroit — l'acte et non l'enfant — et parce qu'un champ « Auteur »
 * s'ajoute, dont le changement peut rendre sans objet le bloc de l'autre parent.
 */
const ModalEvenementReconnaissance = (
  props: ModalEvenementReconnaissanceProps,
) => {
  const { open, identifiant, acte, service, onClose, onSaved } = props;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);
  const [viderNonAuteur, setViderNonAuteur] = React.useState(false);

  const { options: auteurOptions, items: autorites } = useOptionsReferentiel(
    open ? URL_AUTORITES_RECONNAISSANCE : undefined,
  );

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData({
      ..._.pick(acte, CHAMPS_EDITABLES),
      adresseReconnaissance: parseAdresseReconnaissance(acte),
    });
    setAttempted(false);
    setViderNonAuteur(false);
  }, [open, acte]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleTimeInput = React.useMemo(
    () => createTimeInputHandler(handleChange),
    [handleChange],
  );

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      setData((prev) => ({
        ...prev,
        dateReconnaissance: _.isNil(date)
          ? null
          : dayjs(date).format(API_DATE_FORMAT),
      }));
    },
    [],
  );

  /**
   * Le code de l'auteur (`P` / `M` / `PM`) pilote la ventilation côté back : on
   * le dérive de l'identifiant choisi, comme le fait l'assistant.
   */
  const handleAuteurChange = React.useCallback(
    (_event: React.SyntheticEvent | null, payload: { value: unknown }) => {
      const item = _.find(autorites, { identifiant: payload.value });
      setData((prev) => ({
        ...prev,
        identifiantAuteurReconnaissance: payload.value,
        libelleAuteurReconnaissance: item?.libelle,
        codeAuteurReconnaissance: item?.codeJuridique,
      }));
    },
    [autorites],
  );

  /**
   * L'auteur a changé et l'ancien bloc de l'autre parent porte encore une date :
   * c'est à l'agent de décider s'il faut l'effacer, jamais au code.
   */
  const parentAEffacer = React.useMemo(() => {
    const code = data?.codeAuteurReconnaissance;

    if (code === acte?.codeAuteurReconnaissance) {
      return undefined;
    }

    const dateAutreParent =
      code === 'P'
        ? acte?.dateReconnaissanceMere
        : acte?.dateReconnaissancePere;

    return _.isEmpty(dateAutreParent) ? undefined : parentSansObjet(code);
  }, [data?.codeAuteurReconnaissance, acte]);

  const errors = React.useMemo(() => {
    const e: Record<string, string> = {};
    if (_.isEmpty(data?.dateReconnaissance)) {
      e.dateReconnaissance = 'La date de reconnaissance est obligatoire';
    }
    if (_.isNil(data?.identifiantAuteurReconnaissance)) {
      e.identifiantAuteurReconnaissance =
        "L'auteur de la reconnaissance est obligatoire";
    }
    return e;
  }, [data]);

  const displayedErrors = attempted ? errors : {};

  const handleSave = React.useCallback(() => {
    setAttempted(true);
    if (!_.isEmpty(errors)) {
      return;
    }

    const payload = buildEvenementReconnaissanceUpdatePayload(
      identifiant as number,
      data,
      // N'a de sens que si un bloc est effectivement devenu sans objet.
      Boolean(parentAEffacer) && viderNonAuteur,
    );

    setSaving(true);
    service
      ?.update(payload)
      .then(() => {
        onSaved({ ...acte, ...payload.acte });
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de l'événement",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [
    errors,
    service,
    identifiant,
    acte,
    data,
    parentAEffacer,
    viderNonAuteur,
    onSaved,
    onClose,
  ]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header content="Modifier l'événement" />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          <Grid>
            <Grid.Row style={{ marginLeft: '1em', paddingBottom: 0 }}>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <Form.Field
                  error={!_.isNil(displayedErrors.dateReconnaissance)}
                >
                  <label>Reconnu le</label>
                  <Tooltip
                    trigger={
                      <DateInput
                        value={
                          data?.dateReconnaissance
                            ? dayjs(data.dateReconnaissance).toDate()
                            : undefined
                        }
                        onChange={handleDateChange}
                      />
                    }
                    type={displayedErrors.dateReconnaissance ? 'error' : ''}
                    message={displayedErrors.dateReconnaissance}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <Form.Group style={{ alignItems: 'flex-end' }}>
                  <Form.Input
                    label="à"
                    placeholder="hh"
                    name="heureReconnaissance"
                    style={{ width: 48 }}
                    value={
                      !_.isNil(data?.heureReconnaissance)
                        ? String(data.heureReconnaissance).padStart(2, '0')
                        : ''
                    }
                    onChange={handleTimeInput}
                  />
                  <span style={{ alignSelf: 'flex-end', lineHeight: '38px' }}>
                    :
                  </span>
                  <Form.Input
                    placeholder="mm"
                    name="minuteReconnaissance"
                    style={{ width: 48 }}
                    value={
                      !_.isNil(data?.minuteReconnaissance)
                        ? String(data.minuteReconnaissance).padStart(2, '0')
                        : ''
                    }
                    onChange={handleTimeInput}
                  />
                </Form.Group>
              </Grid.Column>
              <Grid.Column computer={9} tablet={16} mobile={16}>
                <Form.Dropdown
                  selection
                  required
                  fluid
                  name="identifiantAuteurReconnaissance"
                  label="Auteur de la reconnaissance"
                  placeholder="Choisir un auteur"
                  options={auteurOptions}
                  value={data?.identifiantAuteurReconnaissance ?? ''}
                  error={
                    !_.isNil(displayedErrors.identifiantAuteurReconnaissance)
                  }
                  onChange={handleAuteurChange}
                />
              </Grid.Column>
            </Grid.Row>
            {parentAEffacer && (
              <Grid.Row style={{ paddingTop: 0 }}>
                <Grid.Column width={16}>
                  <Message warning data-testid="avertissement-non-auteur">
                    <Message.Content>
                      <p>
                        {`Cet acte porte encore une date de reconnaissance pour ${parentAEffacer}, qui n'est plus auteur.`}
                      </p>
                      <Form.Checkbox
                        label={`Effacer les informations de reconnaissance de ${parentAEffacer}`}
                        name="viderNonAuteur"
                        checked={viderNonAuteur}
                        data-testid="vider-non-auteur"
                        onChange={(_e, { checked }) =>
                          setViderNonAuteur(Boolean(checked))
                        }
                      />
                    </Message.Content>
                  </Message>
                </Grid.Column>
              </Grid.Row>
            )}
            <Grid.Row>
              <Grid.Column width={16}>
                <ChampLieu
                  data={data}
                  name="adresseReconnaissance"
                  title="Lieu de la reconnaissance"
                  onChange={handleChange}
                  dateValidite={
                    data?.dateReconnaissance
                      ? dayjs(data.dateReconnaissance).format(API_DATE_FORMAT)
                      : undefined
                  }
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          data-testid="evenement-reconnaissance-enregistrer"
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalEvenementReconnaissance;
